import { z } from 'zod';
import { isDashboardRole } from '../config/dashboardRoles';
import { LEGACY_TENANT_DISPLAY_NAME, LEGACY_TENANT_ID } from '../config/tenant';
import { configuredDisplayName } from '../../../../backend/shared/utils/display-names';
import { displayNameTranslations } from '../utils/translations';
import type { TenantRuntime, TenantProperty } from '../config/tenant';
const https = z.url().refine(value => new URL(value).protocol === 'https:');
const tenantSchema = z.object({ tenantId: z.string(), displayName: z.string().nullish(), status: z.string(), databaseId: z.string(), gatewayUrl: https,
  serviceUrls: z.object({ businessServices: https.optional(), voiceTools: https.optional(), microsoftConnect: https.optional() }).optional(), sharedMailbox: z.string().optional() });
const propertySchema = z.object({ propertyId: z.string(), displayName: z.string().min(1), status: z.literal('active'), code: z.string().regex(/^[A-Za-z0-9]{1,16}$/), houseColor: z.enum(['house-1', 'house-2', 'house-3', 'house-4', 'house-5', 'house-6', 'house-7', 'house-8']), sortOrder: z.number().finite() });
const memberSchema = z.object({ tenantId: z.string(), uid: z.string(), status: z.string(), role: z.enum(['owner', 'manager', 'staff', 'viewer']) });
export interface TenantReader {
  legacyRole(uid: string): Promise<unknown>;
  memberships(uid: string): Promise<unknown[]>;
  tenant(id: string): Promise<unknown>;
  properties(id: string): Promise<unknown[]>;
}
export interface ReachableTenant {
  tenantId: string;
  displayName: string;
}
export type Resolution = ({
  status: 'ready';
  runtime: TenantRuntime;
  reachable: ReachableTenant[];
} | {
  status: 'no-access';
  reachable: ReachableTenant[];
  ended?: string;
} | {
  status: 'incomplete';
  tenantId: string;
  reachable: ReachableTenant[];
}) & {
  membershipsUnavailable?: boolean;
};
/** No operational data are read from the platform database. */
export async function resolveTenant(uid: string, claim: unknown, reader: TenantReader): Promise<Resolution> {
  const selected = claim === undefined ? 'xzllenz' : typeof claim === 'string' ? claim : '';
  const [role, membershipResult] = await Promise.all([reader.legacyRole(uid), reader.memberships(uid).then(value => ({ value, unavailable: false }), () => ({ value: [], unavailable: true }))]);
  const rawMembers = membershipResult.value;
  const notice = { membershipsUnavailable: membershipResult.unavailable };
  const members = rawMembers.map(value => memberSchema.safeParse(value)).filter(result => result.success).map(result => result.data!).filter(member => member.uid === uid && member.status === 'active' && member.tenantId !== 'xzllenz');
  const ids = [...new Set([...(isDashboardRole(role) ? ['xzllenz'] : []), ...members.map(member => member.tenantId)])];
  const records = new Map<string, unknown>();
  const reachable: ReachableTenant[] = [];
  for (const id of ids) {
    if (id === 'xzllenz' && selected !== 'xzllenz') {
      reachable.push({ tenantId: id, displayName: LEGACY_TENANT_DISPLAY_NAME });
      continue;
    }
    const raw = await reader.tenant(id);
    records.set(id, raw);
    const record = raw as {
      status?: string;
      displayName?: string;
    } | null;
    if (record?.status === 'active')
      reachable.push({ tenantId: id, displayName: configuredDisplayName(record.displayName, id)
        ?? (id === LEGACY_TENANT_ID ? LEGACY_TENANT_DISPLAY_NAME : displayNameTranslations.de.tenantMissing) });
  }
  if (!reachable.some(tenant => tenant.tenantId === selected))
    return { ...notice, status: 'no-access', reachable, ...(selected !== 'xzllenz' ? { ended: selected } : {}) };
  const parsed = tenantSchema.safeParse(records.get(selected));
  if (!parsed.success)
    return { ...notice, status: 'incomplete', tenantId: selected, reachable };
  const tenant = parsed.data;
  const legacy = tenant.databaseId === '(default)';
  if (tenant.tenantId !== selected || tenant.databaseId !== (selected === 'xzllenz' ? '(default)' : `tenant-${selected}`)
    || (legacy && (!tenant.serviceUrls?.businessServices || !tenant.serviceUrls.voiceTools || !tenant.serviceUrls.microsoftConnect)))
    return { ...notice, status: 'incomplete', tenantId: selected, reachable };
  let rawProperties: unknown[];
  let propertiesUnavailable = false;
  try {
    rawProperties = await reader.properties(selected);
  }
  catch {
    rawProperties = [];
    propertiesUnavailable = true;
  }
  const parsedProperties = rawProperties.map(value => propertySchema.safeParse(value));
  if (parsedProperties.some(result => !result.success))
    return { ...notice, status: 'incomplete', tenantId: selected, reachable };
  if (!rawProperties.length && !propertiesUnavailable)
    return { ...notice, status: 'incomplete', tenantId: selected, reachable };
  const properties: TenantProperty[] = parsedProperties.map(result => {
    const property = result.data!;
    return { key: property.code, label: property.displayName, color: property.houseColor, propertyId: property.propertyId, sortOrder: property.sortOrder };
  }).sort((a, b) => a.sortOrder - b.sortOrder);
  return { ...notice, status: 'ready', reachable, runtime: { tenantId: selected, displayName: reachable.find(item => item.tenantId === selected)!.displayName, legacy, databaseId: tenant.databaseId, apiBase: tenant.gatewayUrl,
      access: legacy ? 'legacy-role' : 'membership', role: legacy ? role as TenantRuntime['role'] : members.find(member => member.tenantId === selected)!.role,
      serviceUrls: tenant.serviceUrls, sharedMailbox: tenant.sharedMailbox, properties, propertiesUnavailable } };
}
